// The rear dead zone of a gun hull under the approach (R2389, R2391;
// ui-map-interface.md §6.1).
//
// A gun fires only into its gun arc round the bow (`ShipSpec.gun_arc_deg`,
// numbers.md §4.1). The map hatches the rest of the gun-ring disc, the
// part behind the arc, in the same style as the artillery dead zone
// (R1472). This module holds the two rules the fleet layer needs: which
// arc a hull shows now, and the hatch lines of that shape. It keeps no
// state and allocates nothing, so the layer calls it once a frame.

import { AnchorState, SignatureOrder } from '../gen/proto/aquaphobia';
import { TAU } from './math';

/** The fields of the catalog row that the gates read. */
export interface GunArcSpec {
  gunArcDeg: number;
  signature: SignatureOrder;
  anchorRange: number;
}

/**
 * The gun arc in degrees for which the map draws a rear dead zone, or 0
 * for none. The rules follow `gunAllRound` of the sim: a class arc of 0
 * (an unarmed hull) or 360 (the Corsair, the Sentinel), a
 * planted tower, and an active DIG IN or BROADSIDE all fire all round,
 * because BROADSIDE puts its beam test in place of the arc.
 *
 * `digClose` is the share of the DIG IN close of the signature look
 * (R2408, card 18 of round 105), 0 to 1: the arc widens by that share of
 * the rear, so the zone closes from its two edges toward the stern and
 * opens again the same way. A negative figure follows `orderActive`.
 */
export function rearArcDeg(
  spec: GunArcSpec | undefined, anchor: AnchorState, orderActive: boolean, digClose = -1,
): number {
  if (!spec) return 0;
  const arc = spec.gunArcDeg;
  if (!(arc > 0) || arc >= 360) return 0;
  if (anchor === AnchorState.ANCHOR_STATE_SET && spec.anchorRange > 0) return 0;
  if (orderActive && spec.signature === SignatureOrder.SIGNATURE_ORDER_BROADSIDE) return 0;
  if (spec.signature === SignatureOrder.SIGNATURE_ORDER_DIG_IN) {
    const close = digClose >= 0 ? Math.min(1, digClose) : orderActive ? 1 : 0;
    if (close >= 1) return 0;
    return arc + (360 - arc) * close;
  }
  return arc;
}

/** Receives one hatch piece in world px. */
export type HatchEmit = (x0: number, y0: number, x1: number, y1: number) => void;

/** The two edges of the rear zone and its rim arc, in radians. */
export interface RearEdges {
  /** The edge counter-clockwise of the bow: bow + half the arc. */
  left: number;
  /** The edge clockwise of the bow: bow - half the arc. */
  right: number;
  /** The start and the sweep of the rim arc, in the circle batch's sense. */
  start: number;
  sweep: number;
}

/** The edges of the rear zone of a hull with bow `bow` and arc `arcDeg`. */
export function rearEdges(bow: number, arcDeg: number, out: RearEdges): RearEdges {
  const half = arcDeg * Math.PI / 360;
  out.left = bow + half;
  out.right = bow - half;
  out.start = bow + half;
  out.sweep = TAU - 2 * half;
  return out;
}

// The scratch interval of `clip`: a clip writes it, and the caller reads
// it before the next clip.
let lo = 0;
let hi = 0;

/**
 * Clips the scratch interval [lo, hi] on the line c + s·d to the
 * half-plane cross(u, p) >= 0 (sign 1) or cross(p, u) >= 0 (sign -1).
 */
function clip(
  ux: number, uy: number, cx: number, cy: number, dx: number, dy: number, sign: number,
): void {
  const a = sign * (ux * cy - uy * cx);
  const b = sign * (ux * dy - uy * dx);
  if (Math.abs(b) < 1e-12) {
    if (a < 0) hi = lo - 1;
    return;
  }
  const root = -a / b;
  if (b > 0) lo = Math.max(lo, root);
  else hi = Math.min(hi, root);
}

/**
 * The hatch of the rear dead zone round (x, y): the part of the disc of
 * radius `r` outside the gun arc, less the disc of radius `rMin` (the
 * artillery dead zone, which the layer hatches on its own). The lines
 * lean at 45 degrees and sit on the offsets k·step of the artillery
 * hatch, so the two hatches meet line on line. Each line is cut exactly
 * to the shape: the chord of the circle, the two edge rays, and the inner
 * disc. A line can fall into up to three pieces. Returns the number of
 * pieces it emitted.
 */
export function rearHatch(
  x: number, y: number, r: number, rMin: number, bow: number, arcDeg: number,
  step: number, emit: HatchEmit,
): number {
  if (!(r > 0) || !(step > 0) || !(arcDeg > 0) || arcDeg >= 360) return 0;
  const half = arcDeg * Math.PI / 360;
  const lx = Math.cos(bow + half), ly = Math.sin(bow + half);
  const rx = Math.cos(bow - half), ry = Math.sin(bow - half);
  // The line runs along (1, -1) / sqrt 2 and steps along its normal
  // (1, 1) / sqrt 2, as the artillery hatch does.
  const dx = Math.SQRT1_2, dy = -Math.SQRT1_2;
  const narrow = half <= Math.PI / 2;
  const n = Math.floor(r / step);
  let pieces = 0;
  for (let k = -n; k <= n; k++) {
    const t = k * step;
    const h2 = r * r - t * t;
    if (h2 <= 0) continue;
    const h = Math.sqrt(h2);
    const cx = t * Math.SQRT1_2, cy = t * Math.SQRT1_2;
    // A gun arc of half a turn or less is a convex wedge: it is a hole in
    // the chord. A wider arc leaves a convex rear wedge: the chord is
    // clipped to it.
    let g0 = 0, g1 = 0;
    lo = -h; hi = h;
    if (narrow) {
      clip(rx, ry, cx, cy, dx, dy, 1);
      clip(lx, ly, cx, cy, dx, dy, -1);
      g0 = lo; g1 = hi;
      lo = -h; hi = h;
    } else {
      clip(lx, ly, cx, cy, dx, dy, 1);
      clip(rx, ry, cx, cy, dx, dy, -1);
    }
    if (hi <= lo) continue;
    let m0 = 0, m1 = 0;
    if (Math.abs(t) < rMin) {
      const hm = Math.sqrt(rMin * rMin - t * t);
      m0 = -hm; m1 = hm;
    }
    // The holes in start order, then the pieces between them.
    let a0 = m0, a1 = m1, b0 = g0, b1 = g1;
    if (b0 < a0) { a0 = g0; a1 = g1; b0 = m0; b1 = m1; }
    let cur = lo;
    const end = hi;
    for (let j = 0; j < 2; j++) {
      const s0 = j === 0 ? a0 : b0, s1 = j === 0 ? a1 : b1;
      if (s1 <= s0) continue;
      const stop = Math.min(s0, end);
      if (stop - cur > 1e-9) {
        emit(x + cx + dx * cur, y + cy + dy * cur, x + cx + dx * stop, y + cy + dy * stop);
        pieces++;
      }
      cur = Math.max(cur, s1);
    }
    if (end - cur > 1e-9) {
      emit(x + cx + dx * cur, y + cy + dy * cur, x + cx + dx * end, y + cy + dy * end);
      pieces++;
    }
  }
  return pieces;
}
